/**
 * BYK-22: Real-Time Conflict Highlighting & Smart Errors
 *
 * Panel component for displaying all conflicts in a grouped summary.
 */

import { useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import type {
  Conflict,
  ConflictType,
  ConflictSeverity,
  ConflictDetectionResult,
} from "@byk/shared";
import { CONFLICT_COLORS, CONFLICT_TYPE_LABELS } from "@byk/shared";
import { groupConflictsByType } from "@/services/conflictDetection";

interface ConflictsPanelProps {
  result: ConflictDetectionResult;
  onSelectConflict?: (conflict: Conflict) => void;
  onSelectVolume?: (volumeId: string) => void;
  className?: string;
}

/**
 * Summary badge showing counts by severity
 */
const SeveritySummary: React.FC<{
  errors: number;
  warnings: number;
  infos: number;
}> = ({ errors, warnings, infos }) => {
  return (
    <div className="flex items-center gap-2">
      {errors > 0 && (
        <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: CONFLICT_COLORS.error }}
          />
          {errors} error{errors !== 1 ? "s" : ""}
        </span>
      )}
      {warnings > 0 && (
        <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: CONFLICT_COLORS.warning }}
          />
          {warnings} warning{warnings !== 1 ? "s" : ""}
        </span>
      )}
      {infos > 0 && (
        <span className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: CONFLICT_COLORS.info }}
          />
          {infos} info{infos !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
};

/**
 * Individual conflict item
 */
const ConflictItem: React.FC<{
  conflict: Conflict;
  onSelect?: () => void;
  onSelectVolume?: (volumeId: string) => void;
}> = ({ conflict, onSelect, onSelectVolume }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      className={cn(
        "rounded-lg border p-2.5 transition-all",
        conflict.severity === "error" && "border-red-200 bg-red-50",
        conflict.severity === "warning" && "border-amber-200 bg-amber-50",
        conflict.severity === "info" && "border-blue-200 bg-blue-50",
        onSelect && "cursor-pointer hover:shadow-sm"
      )}
      onClick={onSelect}
    >
      {/* Message */}
      <p className="mb-1 text-sm font-medium text-gray-800">{conflict.message}</p>

      {/* Suggestion */}
      <p className="mb-2 text-xs text-gray-600">{conflict.suggestion}</p>

      {/* Affected volumes (if we have volume IDs) */}
      {conflict.volumeIds.length > 0 && onSelectVolume && (
        <div className="flex flex-wrap gap-1">
          {conflict.volumeIds.map((volumeId) => (
            <button
              key={volumeId}
              onClick={(e) => {
                e.stopPropagation();
                onSelectVolume(volumeId);
              }}
              className="rounded bg-white/60 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 hover:bg-white hover:text-gray-700"
            >
              Select item
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

/**
 * Conflict group by type
 */
const ConflictGroup: React.FC<{
  type: ConflictType;
  conflicts: Conflict[];
  isExpanded: boolean;
  onToggle: () => void;
  onSelectConflict?: (conflict: Conflict) => void;
  onSelectVolume?: (volumeId: string) => void;
}> = ({
  type,
  conflicts,
  isExpanded,
  onToggle,
  onSelectConflict,
  onSelectVolume,
}) => {
  // Get the highest severity in this group
  const highestSeverity = useMemo<ConflictSeverity>(() => {
    if (conflicts.some((c) => c.severity === "error")) return "error";
    if (conflicts.some((c) => c.severity === "warning")) return "warning";
    return "info";
  }, [conflicts]);

  const severityColor = CONFLICT_COLORS[highestSeverity];

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white/60">
      {/* Group header */}
      <button
        onClick={onToggle}
        className={cn(
          "flex w-full items-center justify-between px-3 py-2",
          "transition-colors hover:bg-gray-50"
        )}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: severityColor }}
          />
          <span className="text-sm font-medium text-gray-700">
            {CONFLICT_TYPE_LABELS[type]}
          </span>
          <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
            {conflicts.length}
          </span>
        </div>
        <motion.svg
          className="h-4 w-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </motion.svg>
      </button>

      {/* Group content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-2 px-3 pb-3 pt-1">
              {conflicts.map((conflict) => (
                <ConflictItem
                  key={conflict.id}
                  conflict={conflict}
                  onSelect={
                    onSelectConflict ? () => onSelectConflict(conflict) : undefined
                  }
                  onSelectVolume={onSelectVolume}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Main ConflictsPanel component
 */
export const ConflictsPanel: React.FC<ConflictsPanelProps> = ({
  result,
  onSelectConflict,
  onSelectVolume,
  className,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<ConflictType>>(
    new Set(["overlap", "wall-collision"]) // Errors expanded by default
  );

  const groupedConflicts = useMemo(
    () => groupConflictsByType(result.conflicts),
    [result.conflicts]
  );

  const toggleGroup = useCallback((type: ConflictType) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  // Sort groups by severity (errors first)
  const sortedGroups = useMemo(() => {
    const groups = Array.from(groupedConflicts.entries());
    return groups.sort((a, b) => {
      const severityOrder: Record<ConflictSeverity, number> = {
        error: 0,
        warning: 1,
        info: 2,
      };
      const aMaxSeverity = a[1].reduce((max, c) => {
        return severityOrder[c.severity] < severityOrder[max]
          ? c.severity
          : max;
      }, "info" as ConflictSeverity);
      const bMaxSeverity = b[1].reduce((max, c) => {
        return severityOrder[c.severity] < severityOrder[max]
          ? c.severity
          : max;
      }, "info" as ConflictSeverity);
      return severityOrder[aMaxSeverity] - severityOrder[bMaxSeverity];
    });
  }, [groupedConflicts]);

  // If no conflicts, show success state
  if (result.conflicts.length === 0) {
    return (
      <div className={cn("flex flex-col items-center py-6", className)}>
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <svg
            className="h-6 w-6 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-700">No conflicts detected</p>
        <p className="text-xs text-gray-500">Your layout looks great!</p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">
          Layout Issues
          <span className="ml-1.5 text-xs font-normal text-gray-500">
            ({result.conflicts.length})
          </span>
        </h3>
        <SeveritySummary
          errors={result.errorCount}
          warnings={result.warningCount}
          infos={result.infoCount}
        />
      </div>

      {/* Validity status */}
      {!result.isValid && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">
          <svg
            className="h-4 w-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>
            Please resolve {result.errorCount} error
            {result.errorCount !== 1 ? "s" : ""} before proceeding
          </span>
        </div>
      )}

      {/* Conflict groups */}
      <div className="space-y-2">
        {sortedGroups.map(([type, conflicts]) => (
          <ConflictGroup
            key={type}
            type={type}
            conflicts={conflicts}
            isExpanded={expandedGroups.has(type)}
            onToggle={() => toggleGroup(type)}
            onSelectConflict={onSelectConflict}
            onSelectVolume={onSelectVolume}
          />
        ))}
      </div>
    </div>
  );
};

export default ConflictsPanel;
